import { hasNoQuotes } from './apiHelpers';

// A source quote API occasionally fails almost instantly for a request that
// would otherwise succeed on a second attempt — returning a failure payload /
// empty list (or throwing) within a few hundred milliseconds. That "fast
// failure" is a transient glitch, not a genuine "no quotes for you" outcome, so
// we retry it a handful of times. A failure that takes LONGER than the
// threshold is treated as a real result and returned as-is (no retry).
export const FAST_FAILURE_THRESHOLD_MS = 500;
export const FAST_FAILURE_MAX_RETRIES = 3;

/**
 * Wraps a quote-fetching call with fast-failure retry behaviour.
 *
 * @param {() => Promise<any>} makeRequest  Performs one quote request. Should
 *   resolve to the quote payload (or a value `isEmpty` recognises as empty) and
 *   may reject — a rejection is treated as an empty result.
 * @param {object} [options]
 * @param {number} [options.thresholdMs=FAST_FAILURE_THRESHOLD_MS]  Failures that
 *   come back faster than this are considered transient and retried.
 * @param {number} [options.maxRetries=FAST_FAILURE_MAX_RETRIES]  Extra attempts
 *   allowed after the first (so up to maxRetries + 1 requests in total).
 * @param {(result: any) => boolean} [options.isEmpty=hasNoQuotes]  Decides
 *   whether a resolved payload counts as a failure/empty result. Override for
 *   responses that nest their quotes (e.g. `[{ Quotes: [...] }]`).
 * @returns {Promise<any>} The first successful payload, or — once retries are
 *   exhausted or a slow failure occurs — the final failure/empty payload
 *   (`[]` when the underlying request threw).
 */
export async function fetchQuotesWithFastFailureRetry(
  makeRequest,
  {
    thresholdMs = FAST_FAILURE_THRESHOLD_MS,
    maxRetries = FAST_FAILURE_MAX_RETRIES,
    isEmpty = hasNoQuotes,
  } = {}
) {
  for (let attempt = 0; ; attempt += 1) {
    const startedAt = Date.now();

    let result;
    try {
      result = await makeRequest();
    } catch {
      // A thrown error is just another way the request can fail; normalise it
      // to an empty result so the timing check below decides whether to retry.
      result = [];
    }

    if (!isEmpty(result)) return result;

    const failedFast = Date.now() - startedAt < thresholdMs;
    if (failedFast && attempt < maxRetries) continue;

    // Slow failure, or retries exhausted — treat as a genuine "no quotes".
    return result;
  }
}
